import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PrepareQuoteRequest, QuoteResponse } from "./quotesApi";
import { getQuote, prepareQuote } from "./quotesApi";
import { useParams } from "react-router-dom";
import { useSnackbar } from "notistack";

export const useQuoteWithId = (quoteId: string) => {
  const { data: quote, isLoading } = useQuery({
    queryKey: ["quotes", quoteId],
    queryFn: ({ signal }) => getQuote(quoteId, signal),
  });

  return { quote, isLoading };
};

export const useQuote = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { quoteId } = useParams<{ quoteId: string }>();
  if (!quoteId) {
    throw new Error("useQuote requires a quoteId route param");
  }
  const { quote, isLoading } = useQuoteWithId(quoteId);

  const prepareQuoteMutation = useMutation<QuoteResponse, unknown, PrepareQuoteRequest>({
    mutationFn: (r) => prepareQuote(quoteId, r),
    onSuccess: async (data) => {
      enqueueSnackbar("Wycena została zapisana", { variant: "success" });
      queryClient.setQueryData(["quotes", quoteId], data);
      await queryClient.invalidateQueries({ queryKey: ["quotes"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zapisać wyceny. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  return {
    quote,
    isLoading,
    prepareQuote: (request: PrepareQuoteRequest) => prepareQuoteMutation.mutateAsync(request),
  };
};
